// Shared signing/URL helpers for HTTP calls into the DDC S3 gateway's
// auth-domain endpoints (e.g. status / bootstrap). Callers sign over an
// **empty** byte string and identify themselves via three headers; the
// gateway re-verifies the signature against the same empty bytes. We
// keep this surface here so callers don't reimplement the signing
// convention per call site.
//
// Credential registration and revocation are not implemented here — that
// lifecycle is fully delegated to @cef-ai/vault-sdk.

import type { Signer } from '@cef-ai/signer';
import { bytesToHex } from '@/lib/encoding/hex';

export type FetchLike = (input: string | URL | Request, init?: RequestInit) => Promise<Response>;

export type SignedEmptyBytesHeaders = Record<string, string>;

export async function signedEmptyBytesHeaders(signer: Signer): Promise<SignedEmptyBytesHeaders> {
  // Data-plane auth: sign the empty byte string with intent "data" — never
  // raises the wallet consent UI (only "extrinsic" does). Byte-identical to
  // the pre-pivot vault-sdk Wallet-adapter signing path.
  const sig = await signer.sign(new Uint8Array(0), 'data');
  return {
    'X-Public-Key': normalizePubkey(bytesToHex(signer.publicKey)),
    'X-Signature': bytesToHex(sig),
    'X-Signature-Type': signer.type,
  };
}

export function gatewayBase(s3Endpoint: string): string {
  return s3Endpoint.replace(/\/+$/, '');
}

function normalizePubkey(pk: string): string {
  return pk.startsWith('0x') ? pk : `0x${pk}`;
}
